import React, { useState, useEffect } from 'react';
import { Button, Space, Breadcrumb, Row, Col} from 'antd';
import { CardLayout, SortableTable, RefCodeEditModal } from 'components/molecules';
import { useBizCategory } from 'hooks';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { usePopupManager } from 'react-popup-manager';

export interface BizCategoryProps {}

const BizCategory = ({}: BizCategoryProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const [tableDatas, setTableDatas] = useState<any[]>();
  const [subTableDatas, setSubTableDatas] = useState<any[]>();
  const [bizCate, setBizCate] = useState<number>(0);
  const [bizCateName, setBizCateName] = useState<string>('소분류');

  const { open } = usePopupManager();

  const columnsB: ColumnsType<BizCategoryListDto> = [
    {
      key: 'sort',
      width: '70px',
      align: 'center',
    },  
    {
      title: '대분류명',
      dataIndex: 'biz_name',
      key: 'biz_name',
      width: 'auto',
      align: 'left',
    },
    {
      title: '기능',
      key: 'edit',
      width: '80px',
      align: 'center',
      render: (record) => {
        return (
          <Button
            type='default'
            onClick={(e) => {
              e.stopPropagation();

              open(RefCodeEditModal, {
                title: '대분류',      
                ref_code_srl: record.biz_category_srl,
                value: record.biz_name,
                onClose: (isOk, value) => {
                  if (isOk) {
                    bizCategoryUpdate({biz_category_srl: record.biz_category_srl, biz_name: value, main_biz_category_srl: 0})     
                  }
                },
              });               
            }}
          >수정</Button>
        )
      }      
    },
    {
      title: '사용여부',
      dataIndex: 'status_type',
      key: 'status_type',
      width: '80px',
      align: 'center',
      render: (status_type, record) => {
        return (
          <Button
            type='default'
            onClick={(e) => {
              e.stopPropagation();
              bizCategoryUpdate({biz_category_srl: record.biz_category_srl, status_type: status_type === 'AV'?'HD':'AV', main_biz_category_srl: 0})              
            }}
          >{status_type === 'AV' ? 'YES' : 'NO'}</Button>
        )
      }
    }, 
  ];

  const columnsM: ColumnsType<BizCategoryListDto> = [
    {
      key: 'sort',
      width: '70px',
      align: 'center',
    },  
    {
      title: '소분류명',
      dataIndex: 'biz_name',
      key: 'biz_name',
      width: 'auto',
      align: 'left',
    },
    {
      title: '사용여부',
      dataIndex: 'status_type',
      key: 'status_type',
      width: '80px',
      align: 'center',
      render: (status_type, record) => {
        return (
          <Button
            type='default'
            onClick={(e) => {
              e.stopPropagation();
              bizCategoryUpdate({biz_category_srl: record.biz_category_srl, status_type: status_type === 'AV'?'HD':'AV', main_biz_category_srl: record.main_biz_category_srl})                  
            }}
          >{status_type === 'AV' ? 'YES' : 'NO'}</Button>
        )
      }
    }, 
  ];

  /* ======================================================
   * hooks
   * ====================================================== */
  const { bizCateList, bizCateSubList, bizCategorySubList, bizCategoryInsert, bizCategoryUpdate, bizCategorySort } = useBizCategory({main_biz_category_srl:0});    

   /* ======================================================
    * events
    * ====================================================== */    
  const onSort = (active: string | number, over: string | number)  => {
    bizCategorySort({active: active as number, over: over as number, main_biz_category_srl: 0})
  }

  const onSortSub = (active: string | number, over: string | number, activeObj: BizCategoryListDto)  => {
    bizCategorySort({active: active as number, over: over as number, main_biz_category_srl: activeObj.main_biz_category_srl})
  }

  const onRowClick = (record: any) => {
    setBizCate(record.biz_category_srl);
    setBizCateName('소분류 - ' + record.biz_name);
    bizCategorySubList({main_biz_category_srl: record.biz_category_srl}) 
  }  

  const onRowSubClick = (record: any) => {
    open(RefCodeEditModal, {
      title: '소분류',      
      ref_code_srl: record.biz_category_srl,
      value: record.biz_name,
      onClose: (isOk, value) => {
        if (isOk) {
          bizCategoryUpdate({biz_category_srl: record.biz_category_srl, biz_name: value, main_biz_category_srl: record.main_biz_category_srl})     
        }
      },
    }); 
  }  

  const onClickRegister = () => {
    open(RefCodeEditModal, {
      title: '대분류',
      ref_code_srl: 0,
      onClose: (isOk, value) => {
        if (isOk) {
          bizCategoryInsert({
            main_biz_category_srl: 0,
            biz_name: value,
          })    
        }
      },
    }); 
  }

  const onClickRegisterSub = () => {
    open(RefCodeEditModal, {
      title: '소분류',
      ref_code_srl: 0,
      onClose: (isOk, value) => {
        if (isOk) {
          bizCategoryInsert({
            main_biz_category_srl: bizCate,
            biz_name: value,
          })    
        }
      },
    }); 
  }  

   /* ======================================================
    * effects
    * ====================================================== */
   useEffect(() => {
    setTableDatas(bizCateList?.map((item) => ({
      ...item,
      key: item.biz_category_srl,
    })));
  }, [bizCateList]);

  useEffect(() => {
    setSubTableDatas(bizCateSubList?.map((item) => ({
      ...item,
      key: item.biz_category_srl,
    })));
  }, [bizCateSubList]);  

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <CardLayout title={(
      <Breadcrumb>
        <Breadcrumb.Item>Home</Breadcrumb.Item>
        <Breadcrumb.Item>기초정보관리</Breadcrumb.Item>
        <Breadcrumb.Item>업종</Breadcrumb.Item>
    </Breadcrumb>      
    )}>        
      <Row gutter={[18, 24]} align="top">          
        <Col md={12} xs={24}>
          <Space direction="vertical" style={{ width: '100%' }} >
            <TotalNumMenu text={'대분류'} useRegister onUseRegister={onClickRegister}/>
            <SortableTable
              rowSelected
              dataList={tableDatas as any[]}
              columns={columnsB}
              onSort={onSort}
              onClick={onRowClick}
            />                
          </Space>                
        </Col>
        <Col md={12} xs={24}>
          <Space direction="vertical" style={{ width: '100%' }} >
            <TotalNumMenu text={bizCateName} useRegister onUseRegister={onClickRegisterSub}/>
            <SortableTable
              dataList={subTableDatas as any[]}
              columns={columnsM}
              onSort={onSortSub}
              onClick={onRowSubClick}
            />                
          </Space>                
        </Col>        
      </Row>
    </CardLayout>
  )
};

export default BizCategory;    